'use client';

/**
 * ProgressIndicator.tsx — wrapper rundt @carbon/react ProgressIndicator
 *
 * Beholder vår eksisterende API (steps-array med state)
 * men bruker Carbon ProgressIndicator + ProgressStep internt.
 *
 * Carbon-standard som følges:
 * - Anatomi: status-indikator (sirkel), steg-linje, label, helper text
 * - States: completed (hake + blå linje), current (halvfylt sirkel + blå
 *   linje), not-started (kontursirkel + grå linje), invalid (rød)
 * - Vertikal layout anbefales av Carbon når mulig; horisontal i modaler
 * - Ikke-interaktiv som standard (kun visuell progresjon)
 *
 * Bruk:
 *   <ProgressIndicator
 *     steps={[
 *       { label: '1. Detaljer', state: 'completed' },
 *       { label: '2. Kjøretøy', state: 'current' },
 *     ]}
 *   />
 */

import { ProgressIndicator as CarbonProgressIndicator, ProgressStep } from '@carbon/react';

export type ProgressStepState = 'current' | 'completed' | 'not-started' | 'invalid';

export interface ProgressStep {
  label: string;
  state: ProgressStepState;
  helperText?: string;
}

interface ProgressIndicatorProps {
  steps: ProgressStep[];
  /** Horisontal eller vertikal layout */
  orientation?: 'horizontal' | 'vertical';
  ariaLabel?: string;
}

export default function ProgressIndicator({
  steps,
  orientation = 'horizontal',
  ariaLabel = 'Fremdrift',
}: ProgressIndicatorProps) {
  // Finn currentIndex (Carbon bruker indeks, ikke state per steg)
  const currentIndex = steps.findIndex((s) => s.state === 'current');

  return (
    <CarbonProgressIndicator
      vertical={orientation === 'vertical'}
      currentIndex={currentIndex >= 0 ? currentIndex : 0}
      aria-label={ariaLabel}
    >
      {steps.map((step, i) => (
        <ProgressStep
          key={i}
          label={step.label}
          description={step.helperText}
          complete={step.state === 'completed'}
          invalid={step.state === 'invalid'}
        />
      ))}
    </CarbonProgressIndicator>
  );
}